import { mergeProps } from '@base-ui/react/merge-props';
import { useRender } from '@base-ui/react/use-render';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const badgeVariants = cva(
  'tw:group/badge tw:inline-flex tw:h-5 tw:w-fit tw:shrink-0 tw:items-center tw:justify-center tw:gap-1 tw:overflow-hidden tw:rounded-[10px] tw:border tw:border-transparent tw:px-2.5 tw:py-0.5 tw:text-xs tw:font-medium tw:whitespace-nowrap tw:transition-all tw:focus-visible:border-ring tw:focus-visible:ring-[3px] tw:focus-visible:ring-ring/50 tw:has-data-[icon=inline-end]:pr-1.5 tw:has-data-[icon=inline-start]:pl-1.5 tw:aria-invalid:border-destructive tw:aria-invalid:ring-destructive/20 tw:dark:aria-invalid:ring-destructive/40 tw:[&>svg]:pointer-events-none tw:[&>svg]:size-3',
  {
    variants: {
      variant: {
        default: 'tw:bg-primary tw:text-primary-foreground',
        secondary: 'tw:bg-muted tw:text-foreground',
        destructive: 'tw:bg-destructive tw:text-destructive-foreground',
        outline: 'tw:border-border tw:text-foreground',
        // Count/notification pill badges
        count: 'tw:bg-primary tw:text-primary-foreground tw:rounded-full',
        'count-destructive':
          'tw:bg-destructive tw:text-destructive-foreground tw:rounded-full',
        'count-outline': 'tw:border-border tw:text-foreground tw:rounded-full',
        // Named aliases mapped to semantic tokens
        amber: 'tw:border-warning/40 tw:bg-warning/10 tw:text-warning',
        green: 'tw:border-success/40 tw:bg-success/10 tw:text-success',
        orange: 'tw:border-orange-300 tw:bg-orange-50 tw:text-orange-800',
        blue: 'tw:border-info/40 tw:bg-info/10 tw:text-info',
        pink: 'tw:border-secondary/40 tw:bg-secondary/10 tw:text-secondary',
        indigo: 'tw:border-secondary/40 tw:bg-secondary/10 tw:text-secondary',
        cyan: 'tw:border-info/40 tw:bg-info/10 tw:text-info',
        violet: 'tw:border-violet-300 tw:bg-violet-50 tw:text-violet-900',
        teal: 'tw:border-teal-300 tw:bg-teal-50 tw:text-teal-900',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

function Badge({
  className,
  variant = 'default',
  render,
  ...props
}: useRender.ComponentProps<'span'> & VariantProps<typeof badgeVariants>) {
  return useRender({
    defaultTagName: 'span',
    props: mergeProps<'span'>(
      {
        className: cn(badgeVariants({ variant }), className),
      },
      props
    ),
    render,
    state: {
      slot: 'badge',
      variant,
    },
  });
}

export { Badge, badgeVariants };
